import React, { useState, useEffect, useRef } from "react";
import { TopBar } from "../../shared/topBar/TopBar";
import { UrlPath } from "../../../config/routeConfig";
import { BreadCrumbs } from "../../shared/breadCrumbs/BreadCrumbs";
import { toast, ToastContainer } from "react-toastify";
import { sbaAxios } from "../../utils/sbaAxios";
import * as XLSX from "xlsx";
import {
  SBAComboBox,
  SBADropDown,
  SBAMultiSelect,
} from "../../UiComponents/SBADropDown";
import { SBADrawer } from "../../UiComponents/SBADrawer";
import { AgGridReact } from "ag-grid-react";
import { Modal } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { 
  checkHealth,
  ingestCacheFile,
  genAIUploadFile, 
  flushCache,
  getLatestTrainingFile,
  insertTrainingFile,
  getTrainingFileList,
  getTrainingFileById,
 } from "../../../config/apiConfig";
 import { saveAs } from "file-saver";
import { PDFExport } from "@progress/kendo-react-pdf";

const TrainModel = () => {
  const Level = [{ BreadCrumb: "Admin >", Routes: `${UrlPath.Admin}` }, {BreadCrumb:"Train Model", Routes:`${UrlPath.Custodians}`}];

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [editRowIndex, setEditRowIndex] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [queryInput, setQueryInput] = useState("");
  const [sqlInput, setSqlInput] = useState("");
  const [upsertMode, setUpsertMode] = useState("Add");
  const [tempData, setTempData] = useState(null);
  const [setOpen, setOpenModal] = useState(false);
  const [isLatestFileFetched, setIsLatestFileFetched] = useState(false);
  const pdfExportComponent = useRef(null);
  const [trainingFilesList, setTrainingFilesList] = useState([]);
  const [selectedTrainingFile, setSelectedTrainingFile] = useState(null);

  const exportToPDF = () => {
      if (pdfExportComponent.current) {
          pdfExportComponent.current.save();
      } else {
          toast.error("Failed to generate PDF.");
      }
  };

  const formatAndSortFiles = (files) => {
    return files
      .map(file => {
        const date = new Date(file.UploadedAt);
  
        // Format date to readable format without decimal part of seconds
        const formattedDate = date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(',', '');
  
        return {
          ...file,
          FormattedDate: formattedDate
        };
      })
      .sort((a, b) => new Date(b.UploadedAt) - new Date(a.UploadedAt));  // Sort descending
  };

  const fetchTrainingFilesList = async() => {
    try{
      const list = await sbaAxios({
        url: getTrainingFileList
      });
      const updatedList = formatAndSortFiles(list);
      setTrainingFilesList(updatedList);
      setSelectedTrainingFile(updatedList[0]);
    }
    catch(e){
      setTrainingFilesList([]);
      setSelectedTrainingFile(null);
    }
  };

  useEffect(()=>{
    document.title="Train Model";
  },[]);

  useEffect(()=>{
    fetchTrainingFilesList();
    if(!isLatestFileFetched){
      fetchLatestTrainingFile();
    }
  },[]);

  useEffect(() => {
    if(selectedTrainingFile){
      fetchTrainingFileById();
    }
  },[selectedTrainingFile]);

  const addRow = () => {
    if(rowData.length === 0){
        toast.error('Please Upload the excel file');
        return;
    }
    // gridRef.current.api.stopEditing();
    // setRowData((prevData) => [...prevData, { id: prevData.length + 1, question: "", query: "" }]);
    // setEditRowIndex(rowData.length);
    setQueryInput("");
    setSqlInput("");
    setUpsertMode("Add");
    setDrawerOpen(true);
};

  const handleCheckHealth = async() => {
    try{
        await sbaAxios({
            url: checkHealth
        });
        toast.success('GenAI service is running');
    }
    catch(e){
        toast.error('GenAI service is not running');
    }
  };

  const handleLoadCache = async() => {
    try{
        await sbaAxios({
            url:ingestCacheFile,
            // method:'post'
        });
        toast.success('Cache Loaded!.');
    }
    catch(e){
        toast.error('Unable to load Cache!.')
    }
  };
  const handleFlushache = async() => {
    try{
        await sbaAxios({
            url:flushCache,
            // method:'post'
        });
        toast.success('Cache Flushed!.');
    }
    catch(e){
        toast.error('Unable Flush Cache!.')
    }
  };

  const fetchTrainingFileById = async () => {
    try {
        const response = await sbaAxios({
          url: `${getTrainingFileById}/${selectedTrainingFile.FileId}`
        });
        
        if (response && response.FileName && response.FileData) {
            const { FileName, FileData } = response;

            // Convert byte array (base64 string) to Blob
            const byteCharacters = atob(FileData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

            // Read the Excel file
            const reader = new FileReader();
            reader.onload = (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                // Convert Excel data into AG Grid format
                const formattedData = jsonData.slice(1).map((row) => ({
                    question: row[0] || "",
                    query: row[1] || ""
                }));

                setRowData(formattedData);
            };

            reader.readAsArrayBuffer(blob);
        } else {
            toast.error("No file data found.");
        }
        setIsLatestFileFetched(true);
    } catch (error) {
        console.error("Error fetching latest training file:", error);
        toast.error("Failed to load training file.");
        setIsLatestFileFetched(false)
    }
};

  const fetchLatestTrainingFile = async () => {
    try {
        const response = await sbaAxios({
          url: getLatestTrainingFile
        });
        
        if (response && response.FileName && response.FileData) {
            const { FileName, FileData } = response;

            // Convert byte array (base64 string) to Blob
            const byteCharacters = atob(FileData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

            // Read the Excel file
            const reader = new FileReader();
            reader.onload = (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                // Convert Excel data into AG Grid format
                const formattedData = jsonData.slice(1).map((row) => ({
                    question: row[0] || "",
                    query: row[1] || ""
                }));

                setRowData(formattedData);
            };

            reader.readAsArrayBuffer(blob);
        } else {
            toast.error("No file data found.");
        }
        setIsLatestFileFetched(true);
    } catch (error) {
        console.error("Error fetching latest training file:", error);
        toast.error("Failed to load training file.");
        setIsLatestFileFetched(false)
    }
};

  const handleUpload = async () => {
    if (!rowData || rowData.length === 0) {
        toast.error("No data available to upload");
        return;
    }

    // Extract only 'questions' and 'query' columns
    const filteredData = rowData.map(row => ({
        QUESTION: row.question || "", 
        QUERY: row.query || ""  
    }));

    // Convert to Excel
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    // Generate Excel file as a Blob
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

    // Create FormData and append file
    const formData = new FormData();
    formData.append("file", blob, "questions.xlsx");

    try{
      await sbaAxios({
        url: insertTrainingFile,
        method: 'post',
        data: formData,
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    toast.success("Training file saved");
    }catch (error) {
      toast.error("Unable to save Training file");
  }

    try {
        await sbaAxios({
            url: genAIUploadFile,
            method: 'post',
            data: formData,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        toast.success("Training file uploaded successfully");
    } catch (error) {
        toast.error("Unable to upload Training file");
    }

    setTimeout(() => {
      window.location.reload();
  }, 2000);
};


const downloadExcel = () => {
    if (!rowData || rowData.length === 0) {
        alert("No data to download.");
        return;
    }

    const transformedData = rowData.map(row => {
      const newRow = {};
      Object.keys(row).forEach(key => {
          newRow[key.toUpperCase()] = row[key]; // Convert keys to uppercase
      });
      return newRow;
  });

    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    // Generate Excel file as a Blob
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

    // Create a download link and trigger it
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modified_data.xlsx";
    document.body.appendChild(a);
    a.click();

    // Cleanup
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setTimeout(() => setOpenModal(true), 1150);
};

const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

        if (sheet.length === 0) {
            alert("Excel file is empty or has incorrect format.");
            return;
        }

        // Convert headers to lowercase and map them
        const formattedData = sheet.map((row) => {
            const normalizedRow = {};
            Object.keys(row).forEach((key) => {
                const lowerKey = key.toLowerCase(); // Convert headers to lowercase
                if (lowerKey === "question") normalizedRow["question"] = row[key];
                if (lowerKey === "query") normalizedRow["query"] = row[key];
            });

            return {
                question: normalizedRow["question"] || "",
                query: normalizedRow["query"] || "",
            };
        });

        setRowData([...formattedData]); // Update state
    };
    reader.readAsBinaryString(file);
};


const startEdit = (params) => {
    // const rowIndex = params.node.rowIndex;
    // setEditRowIndex(rowIndex);
    // setTempData({ ...params.data });

    // setTimeout(() => {
    //     gridRef.current.api.startEditingCell({
    //         rowIndex,
    //         colKey: "question",
    //     });
    // }, 100);
    const rowIndex = params.node.rowIndex;
    setQueryInput(params.data.question);
    setSqlInput(params.data.query);
    setEditRowIndex(rowIndex);
    setUpsertMode("Edit");
    setDrawerOpen(true);
};

const handleUpsert = () => {
  if (!queryInput || !sqlInput) {
    toast.error("Query and SQL cannot be empty");
    return;
  }

  if (upsertMode === "Add") {
    setRowData((prevData) => [
      ...prevData,
      { id: prevData.length + 1, question: queryInput, query: sqlInput }
    ]);
  } else if (upsertMode === "Edit" && editRowIndex !== null) {
    setRowData((prevData) =>
      prevData.map((row, idx) => (idx === editRowIndex ? { ...row, question: queryInput, query: sqlInput } : row))
    );
  }

  setDrawerOpen(false);
  setEditRowIndex(null);
};

const saveEdit = () => {
    gridRef.current.api.stopEditing();
    setEditRowIndex(null);
};

const cancelEdit = () => {
    setRowData((prevData) =>
        prevData.map((row, idx) => (idx === editRowIndex ? { ...tempData } : row))
    );
    setEditRowIndex(null);
};


const deleteRow = (params) => {
  setRowData(rowData.filter((_, index) => index !== params.node.rowIndex));
};

const columnDefs = [
  { headerName: "Question", field: "question", flex: 1 },
  { headerName: "Query", field: "query", flex: 1 },
  {
    headerName: "Actions",
    field: "actions",
    cellRenderer: (params) => (
      <div className="actions-cell">
        <i className="fas fa-edit" style={{ color: "#47A547", margin: "0 8px", cursor: "pointer" }} onClick={() => startEdit(params)}></i>
        <i className="fa fa-trash" style={{ color: "red", margin: "0 8px", cursor: "pointer" }} onClick={() => deleteRow(params)}></i>
      </div>
    ),
    flex: 0.2,
  },
];


  return (
    <>
      <TopBar label={"Train Model"} />
      <div className="mainContentData">
        <BreadCrumbs Levels={Level} />
        <div className="container-fluid p-4">
          <div className="row">
            <div className="col-md-4">
            <label className="label" >Training Files</label>
                    <div>
                        <SBAComboBox
                            className="w-100"
                            data={trainingFilesList}
                            textField={"FormattedDate"}
                            valueField={"FileId"}
                            value={selectedTrainingFile}
                            onChange={(e) => setSelectedTrainingFile(e.value)}
                            popupSettings={{ className: "modalDD" }}
                        ></SBAComboBox>
                    </div>
            </div>
          </div>
          <div className="row mt-4">
                <div className="d-flex justify-content-end align-items-center gap-2 ">
                    <i 
                      className="fa-solid fa-circle-info fs-4" 
                      onClick={exportToPDF}
                      style={{ cursor: "pointer", position:'relative', bottom:"5px" }} 
                      data-bs-toggle="tooltip" 
                      data-bs-placement="top" 
                      title="Help"
                    ></i>
                    <label htmlFor="file-upload">
                    <input id="file-upload" type="file" accept=".xlsx, .xls" onChange={handleFileUpload} style={{ display: "none" }} />
                    <button 
                      variant="contained" 
                      className='p-sm-btn mb-2' 
                      onClick={() => document.getElementById("file-upload").click()}
                      data-bs-toggle="tooltip" 
                      data-bs-placement="top" 
                      title="Click to upload an excel file to be shown in grid"
                    >Upload Excel</button>
                    </label>
                    <button 
                      className='p-sm-btn mb-2' 
                      onClick={addRow}
                      data-bs-toggle="tooltip" 
                      data-bs-placement="top" 
                      title="Click to add a new row into the grid"
                    >Add Row</button>
                    <button 
                      className='p-sm-btn mb-2' 
                      onClick={downloadExcel}
                      data-bs-toggle="tooltip" 
                      data-bs-placement="top" 
                      title="Click to download grid data as excel file"
                    >Download</button>
                    <label htmlFor="train-file-upload">
                    <button 
                      variant="contained" 
                      className='p-sm-btn mb-2' 
                      onClick={handleUpload}
                      data-bs-toggle="tooltip" 
                      data-bs-placement="top" 
                      title="Click to train the model with the data in the grid"
                    >Train Model</button>
                    </label>
                    <button 
                      className='p-sm-btn mb-2' 
                      onClick={handleCheckHealth}
                      data-bs-toggle="tooltip" 
                      data-bs-placement="top" 
                      title="Click to check the status of GenAI server"
                    >Check Health</button>
                    <button 
                      className='p-sm-btn mb-2' 
                      onClick={handleLoadCache}
                      data-bs-toggle="tooltip" 
                      data-bs-placement="top" 
                      title="Click to Load the cached excel file into GenAI model"
                    >Load Cache</button>
                    <button 
                      className='p-sm-btn mb-2' 
                      onClick={handleFlushache}
                      data-bs-toggle="tooltip" 
                      data-bs-placement="top" 
                      title="Click to remove the cached excel file from GenAI model"
                    >Flush Cache</button>
                </div>
          </div>
          <div className="row mt-2">
          <div
            className="ag-theme-alpine"
            style={{ height: "500px", width: "100%", paddingTop: 0 }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                flex: 1,
              }}
            />
          </div>
        </div>
        </div>
        
      </div>
      <SBADrawer open={drawerOpen} setOpen={setDrawerOpen} title={upsertMode === "Add" ? "Add Query" : "Edit Query"} style={{ width: "40vw" }}>
        <div className="row">
          <label className="label">Question</label>
            <div style={{padding:"10px"}}>
              <textarea rows={3} className="w-100 input-default form-control" value={queryInput} onChange={(e) => setQueryInput(e.target.value)} />
            </div>
        </div>
        <div className="row mt-4">
          <label className="label">Query</label>
          <div style={{padding:"10px"}}>
          <textarea rows={15} className="w-100 input-default form-control" value={sqlInput} onChange={(e) => setSqlInput(e.target.value)} />
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="p-sm-btn mx-1" onClick={handleUpsert}>{upsertMode === "Add" ? "Save" : "Update"}</button>
          <button className="s-sm-btn mx-1" onClick={() => setDrawerOpen(false)}>Cancel</button>
        </div>
      </SBADrawer>
      <ToastContainer />
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
            <PDFExport ref={pdfExportComponent} paperSize="A4" fileName={"Help.pdf"}>
                <div id={"pdf export"} style={{padding:"20px"}}>
                    {/* This is where you render the HTML content */}
                    <h2>GenAI Training Manual</h2>
                    <p style={{padding:"10px"}}>This manual explains the functionality of each button on the <strong>Train Model</strong> page.</p>

                    <h5>1. Upload Excel</h5>
                    <p style={{padding:"10px"}}>
                        <strong>Function:</strong> Opens a file picker dialog to upload an Excel file (.xlsx, .xls) 
                        and displays the data in the grid.<br />
                        <strong>Tooltip:</strong> "Click to upload an excel file to be shown in grid"
                    </p>

                    <h5>2. Add Row</h5>
                    <p style={{padding:"10px"}}>
                        <strong>Function:</strong> Adds a new row to the grid where you can input data.<br />
                        <strong>Tooltip:</strong> "Click to add a new row into the grid"
                    </p>

                    <h5>3. Download</h5>
                    <p style={{padding:"10px"}}>
                        <strong>Function:</strong> Downloads the current grid data as an Excel file.<br />
                        <strong>Tooltip:</strong> "Click to download grid data as excel file"
                    </p>

                    <h5>4. Train Model</h5>
                    <p style={{padding:"10px"}}>
                        <strong>Function:</strong> Sends the current grid data to the GenAI model for training.<br />
                        <strong>Tooltip:</strong> "Click to train the model with the data in the grid"
                    </p>

                    <h5>5. Check Health</h5>
                    <p style={{padding:"10px"}}>
                        <strong>Function:</strong> Checks the health/status of the GenAI server to ensure it is operational.<br />
                        <strong>Tooltip:</strong> "Click to check the status of GenAI server"
                    </p>

                    <h5>6. Load Cache</h5>
                    <p style={{padding:"10px"}}>
                        <strong>Function:</strong> Loads the cached Excel file into the GenAI model.<br />
                        <strong>Tooltip:</strong> "Click to Load the cached excel file into GenAI model"
                    </p>

                    <h5>7. Flush Cache</h5>
                    <p style={{padding:"10px"}}>
                        <strong>Function:</strong> Removes the cached Excel file from the GenAI model.<br />
                        <strong>Tooltip:</strong> "Click to remove the cached excel file from GenAI model"
                    </p>
                </div>
            </PDFExport>

            {/* <button onClick={exportToPDF}>Download PDF</button> */}
        </div>
    </>
  );
};


export default TrainModel;
